if (!customElements.get('video-steps')) {
  class VideoSteps extends HTMLElement {
    constructor() {
      super();
      this.currentVideoIndex = 0;
      this.videos = [];
      this.progressInterval = null;
      this.isMobile = window.innerWidth <= 768;
      this.handleScrollDebounced = this.debounce(this.handleScroll.bind(this), 100);
      this.selectors = {
        videoItem: '.video-steps__content-item-video',
        videoWrapper: '.video-steps__content-item-video-wrapper',
        progressElement: '.video-steps__content-item-video-progress',
        videoContent: '.video-steps__content',
      };
    }

    connectedCallback() {
      this.render();
      this.addResizeListener();
    }

    render() {
      this.videos = [...this.querySelectorAll(this.selectors.videoItem)]
                         .filter(video => window.getComputedStyle(video).display !== 'none');

      if (!this.videos.length) return;

      this.currentVideoIndex = Math.min(this.currentVideoIndex, this.videos.length - 1);
      this.playVideo(this.currentVideoIndex);

      if (this.isMobile) {
        this.addScrollListener();
      } else {
        this.addClickListeners();
      }
    }

    debounce(func, delay) {
      let timeoutId;

      return function (...args) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          func.apply(this, args);
        }, delay);
      };
    }

    handleVideoClick(index) {
      if (this.currentVideoIndex === index) return;
      this.stopCurrentVideo();
      const previousIndex = this.currentVideoIndex;

      this.currentVideoIndex = index;
      this.playVideo(this.currentVideoIndex);
      this.markVideosAsPlayed(previousIndex, this.currentVideoIndex);
    }

    markVideosAsPlayed(fromIndex, toIndex) {
      if (fromIndex < toIndex) {
        for (let i = fromIndex; i < toIndex; i++) {
          const video = this.videos[i];

          video.pause();
          video.currentTime = video.duration;
          const progressElement = video.closest(this.selectors.videoWrapper)?.querySelector(this.selectors.progressElement);

          if (progressElement) {
            progressElement.style.setProperty('--progress-width', '100%');
          }
        }
      } else {
        for (let i = fromIndex; i > toIndex; i--) {
          const video = this.videos[i];

          video.pause();
          video.currentTime = 0;
          const progressElement = video.closest(this.selectors.videoWrapper)?.querySelector(this.selectors.progressElement);

          if (progressElement) { progressElement.style.setProperty('--progress-width', '0%'); }
        }
      }
    }

    stopCurrentVideo() {
      const currentVideo = this.videos[this.currentVideoIndex];
      const progressElement = currentVideo.closest(this.selectors.videoWrapper)?.querySelector(this.selectors.progressElement);

      currentVideo.pause();
      currentVideo.currentTime = currentVideo.duration;

      if (progressElement) { progressElement.style.setProperty('--progress-width', '100%'); }

      clearInterval(this.progressInterval);
    }

    playVideo(index) {
      const video = this.videos[index];
      const progressWrapper = video.closest(this.selectors.videoWrapper);
      const progressElement = progressWrapper?.querySelector(this.selectors.progressElement);

      clearInterval(this.progressInterval);
      if (progressElement) { progressElement.style.setProperty('--progress-width', '0%'); }

      if (video && window.getComputedStyle(video).display !== 'none') {
        video.muted = true;
        video.currentTime = 0;
        video.play().catch(error => {});

        const videoDuration = parseInt(video.dataset.duration, 10) || (video.duration * 1000);
        const updateInterval = 10;

        this.progressInterval = setInterval(() => {
          const currentTime = video.currentTime * 1000;
          const progress = (currentTime / videoDuration) * 100;

          if (progressElement) { progressElement.style.setProperty('--progress-width', `${progress}%`); }

          if (progress >= 100) { clearInterval(this.progressInterval); }
        }, updateInterval);

        video.onended = () => {
          this.currentVideoIndex = (index + 1) % this.videos.length;
          this.playVideo(this.currentVideoIndex);
          this.scrollToVideo(this.currentVideoIndex);
          this.markVideosAsPlayed(this.videos.length - 1, this.currentVideoIndex);
        };
      }
    }

    scrollToVideo(index) {
      const video = this.videos[index];
      if (video) {
        const wrapper = this.closest(this.selectors.videoContent);

        if (wrapper) {
          const videoOffsetLeft = video.offsetLeft;

          wrapper.scrollTo({ left: videoOffsetLeft - 16, behavior: 'smooth' });
        }
      }
    }

    handleScroll() {
      const wrapper = this.closest(this.selectors.videoContent);

      if (!wrapper) return;

      let maxVisibleIndex = -1;
      let maxVisibleArea = 0;

      this.videos.forEach((video, index) => {
        const rect = video.getBoundingClientRect();
        const wrapperRect = wrapper.getBoundingClientRect();
        const visibleArea = Math.max(0, Math.min(rect.right, wrapperRect.right) - Math.max(rect.left, wrapperRect.left));
        const isVideoMostlyVisible = visibleArea / rect.width >= 0.5;

        if (visibleArea > maxVisibleArea && isVideoMostlyVisible) {
          maxVisibleArea = visibleArea;
          maxVisibleIndex = index;
        }
      });

      if (maxVisibleIndex !== -1 && this.currentVideoIndex !== maxVisibleIndex) {
        const currentVideo = this.videos[maxVisibleIndex];

        if (currentVideo && window.getComputedStyle(currentVideo).display !== 'none') {
          this.stopCurrentVideo();
          this.currentVideoIndex = maxVisibleIndex;
          this.playVideo(currentVideo.dataset.index);
          this.markVideosAsPlayed(this.videos.length - 1, maxVisibleIndex);
        }
      }
    }

    addClickListeners() {
      this.videos.forEach((video, index) => { video.addEventListener('click', () => this.handleVideoClick(index)); });
    }

    removeClickListeners() { this.videos.forEach(video => video.removeEventListener('click', this.handleVideoClick)); }

    addScrollListener() {
      const wrapper = this.closest(this.selectors.videoContent);

      if (wrapper) {
        wrapper.addEventListener('scroll', this.handleScrollDebounced, { passive: false });
      }
    }

    removeScrollListener() {
      const wrapper = this.closest(this.selectors.videoContent);

      if (wrapper) {
        wrapper.removeEventListener('scroll', this.handleScrollDebounced);
      }
    }

    addResizeListener() { window.addEventListener('resize', this.handleResize.bind(this)); }

    handleResize() {
      const isNowMobile = window.innerWidth <= 768;

      if (isNowMobile !== this.isMobile) {
        this.isMobile = isNowMobile;
        this.toggleListeners();
      }
    }

    toggleListeners() {
      if (this.isMobile) {
        this.removeClickListeners();
        this.addScrollListener();
      } else {
        this.removeScrollListener();
        this.addClickListeners();
      }
    }
  }

  customElements.define('video-steps', VideoSteps);
}

function createObserverForElements() {
  const elements = document.querySelectorAll('video-steps');
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const element = entry.target;

        element.render();
        observer.unobserve(element);
      }
    });
  }, { threshold: 0.1 });

  elements.forEach(element => observer.observe(element));
}

document.addEventListener("DOMContentLoaded", () => { createObserverForElements(); });
